import { InboxOutlined, UserOutlined } from '@ant-design/icons';
import { App, Button, Form, Input, InputNumber, Select, Spin } from 'antd';
import Countdown from 'components/Countdown/Countdown';
import Header from 'components/Header/Header';
import NavBar from 'components/NavBar';
import RightGif from 'components/RightGif';
import WorldCupPredictionBlock from 'components/WorldCupPrediction/WorldCupPredictionBlock';
import { SITE } from 'config/config';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { createPredictions, getFP, getIP, getMatchList, getTime } from 'service/base';
import { twMerge } from 'tailwind-merge';
import { CONST_SITE } from 'utils/common/const';

const PredictPage = () => {
  const { notification } = App.useApp();
  const [form] = Form.useForm();
  const worldCupRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [isPredictConfirmOpen, setIsPredictConfirmOpen] = useState(false);
  const [pendingMatchPredictions, setPendingMatchPredictions] = useState([]);
  const [listDataMatch, setListDataMatch] = useState([]);
  const [timeServer, setTimeServer] = useState(null);
  const [IP, setIP] = useState(null);
  const [FP, setFP] = useState(null);
  const [predictionMode, setPredictionMode] = useState('match'); // 'match' | 'worldcup'

  // Danh sách trận đấu hiển thị
  const getFilteredMatches = () => {
    return listDataMatch || [];
  };

  useEffect(() => {
    fetchIP();
    fetchFP();
    fetchTimeServer();

    // Load saved account from localStorage
    const savedAccount = localStorage.getItem('prediction_account');
    if (savedAccount) {
      form.setFieldsValue({ account: savedAccount });
    }
  }, [form]);

  useEffect(() => {
    fetchDataScheduByLS();
  }, []);

  const fetchIP = async () => {
    try {
      const res = await getIP();
      setIP(res);
    } catch (e) {}
  };

  const fetchFP = async () => {
    try {
      const res = await getFP();
      setFP(res);
    } catch (e) {}
  };

  const fetchTimeServer = async () => {
    try {
      const res = await getTime();
      setTimeServer(res);
    } catch (e) {}
  };

  const fetchDataScheduByLS = async () => {
    try {
      setIsLoading(true);
      const res2 = await getMatchList({
        site: CONST_SITE.Jun88K36
      });

      const dataMatch2 =
        res2?.data?.length > 0
          ? res2.data.map((item) => {
              const formatCountDown = dayjs.unix(item.startTime);
              return { ...item, countdown: new Date(formatCountDown) };
            })
          : [];

      setListDataMatch(dataMatch2 || []);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching match list:', error);
      setListDataMatch([]);
      setIsLoading(false);
    }
  };

  const TIMEOUT_DURATION = 10000; // 10 seconds

  const createPredictionsWithTimeout = (predictions) => {
    return Promise.race([
      createPredictions(predictions),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out')), TIMEOUT_DURATION)
      )
    ]);
  };

  const onFinishForm = async (data) => {
    try {
      if (!data?.account) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng nhập tên đăng nhập!'
        });
        return;
      }

      // World Cup: gửi qua nút «Dự đoán» trong từng tab (WorldCupPredictionBlock)
      if (predictionMode === 'worldcup') {
        // notification.info({
        //   message: 'Thông báo',
        //   description:
        //     'Chọn tab Đội vô địch hoặc Vua phá lưới, chọn đội/cầu thủ rồi nhấn nút «Dự đoán» trong tab đó.'
        // });
        // return;
      }

      // Handle Match prediction (existing logic)
      const filteredMatches = getFilteredMatches();
      if (filteredMatches?.length === 0) return;

      // Validate that both home and away scores are provided for each match
      const hasInvalidScores = Object.entries(data).some(([key, value]) => {
        // Bỏ qua field account và các field không phải score
        if (key === 'account' || !key.includes('_')) return false;

        const parts = key.split('_');
        if (parts.length !== 2) return false;

        const [scoreType, matchId] = parts;
        if (!['homeScore', 'awayScore'].includes(scoreType)) return false;

        const otherScoreType = scoreType === 'homeScore' ? 'awayScore' : 'homeScore';
        const otherScoreKey = `${otherScoreType}_${matchId}`;
        const otherValue = data[otherScoreKey];

        // Kiểm tra nếu một trong hai score có giá trị nhưng score kia không có
        const hasValue = value !== undefined && value !== null && value !== '';
        const hasOtherValue = otherValue !== undefined && otherValue !== null && otherValue !== '';

        if (hasValue && !hasOtherValue) {
          return true;
        }

        return false;
      });

      if (hasInvalidScores) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng nhập đầy đủ cả hai bên tỉ số cho mỗi trận đấu!'
        });
        return;
      }

      console.log('Submitting data:', data);
      console.log('Filtered Matches:', filteredMatches);
      const resultGroup = groupScoresByMatchId(data, IP, FP, filteredMatches);
      console.log('Grouped Results:', resultGroup);

      if (resultGroup.length === 0) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng dự đoán ít nhất một trận đấu!'
        });
        return;
      }

      // Open confirm popup before submitting
      setPendingMatchPredictions(resultGroup);
      setIsPredictConfirmOpen(true);
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: error.message || 'Đã xảy ra lỗi khi gửi dự đoán!'
      });
      setIsLoadingSubmit(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    if (!errorInfo?.values?.account) {
      notification.warning({
        message: 'Cảnh báo',
        description: 'Vui lòng nhập tên đang nhập!'
      });
    } else {
      notification.warning({
        message: 'Cảnh báo',
        description: 'Vui lòng điền đầy đủ thông tin!'
      });
    }
  };

  const onCancelMatchConfirm = () => {
    setIsPredictConfirmOpen(false);
  };

  const onConfirmMatchPrediction = async () => {
    if (pendingMatchPredictions.length === 0) {
      setIsPredictConfirmOpen(false);
      return;
    }

    try {
      setIsLoadingSubmit(true);

      const res = await createPredictionsWithTimeout(pendingMatchPredictions);
      if (res?.code === 0) {
        // Save account to localStorage on success
        const account = pendingMatchPredictions[0]?.account;
        if (account) {
          localStorage.setItem('prediction_account', account);
        }

        notification.success({
          message: 'Thành công',
          description: 'Dự đoán tỉ số thành công!'
        });
        form.resetFields(Object.keys(form.getFieldsValue()).filter((key) => key !== 'account'));
        setIsPredictConfirmOpen(false);
        setPendingMatchPredictions([]);
        // Reload match list
        fetchDataScheduByLS();
      } else {
        if (res.valid === false) {
          notification.error({
            message: res.title_mess,
            description: res?.text_mess
          });
        } else {
          notification.error({
            message: 'Lỗi',
            description: res?.message
          });
        }
      }
      setIsLoadingSubmit(false);
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: error.message || 'Đã xảy ra lỗi khi gửi dự đoán!'
      });
      setIsLoadingSubmit(false);
    }
  };

  const groupScoresByMatchId = (input, ip, fp, matchInfo) => {
    const groupedMatches = Object.entries(input).reduce((acc, [key, value]) => {
      console.log('Processing key:', key, 'with value:', value);
      if (key === 'account') return acc;

      const parts = key.split('_');
      if (parts.length !== 2) return acc;

      const [scoreType, matchId] = parts;
      if (!matchId || !['homeScore', 'awayScore', 'firstGoal'].includes(scoreType)) return acc;

      // Kiểm tra giá trị hợp lệ cho homeScore và awayScore
      if (['homeScore', 'awayScore'].includes(scoreType)) {
        if (value === undefined || value === null || value === '') return acc;
      }

      if (!acc[matchId]) {
        acc[matchId] = {
          matchId,
          account: input.account,
          homeScore: undefined,
          awayScore: undefined,
          firstGoal: undefined
        };
      }

      if (scoreType === 'homeScore') {
        acc[matchId].homeScore = value;
      } else if (scoreType === 'awayScore') {
        acc[matchId].awayScore = value;
      } else if (scoreType === 'firstGoal') {
        // Chỉ thêm firstGoal nếu value không rỗng
        if (value !== undefined && value !== null) {
          acc[matchId].firstGoal = value;
        }
      }

      return acc;
    }, {});

    console.log('Grouped Matches:', groupedMatches);

    return Object.values(groupedMatches)
      .filter(
        (match) =>
          match.homeScore !== undefined &&
          match.awayScore !== undefined &&
          match.homeScore !== null &&
          match.awayScore !== null &&
          match.homeScore !== '' &&
          match.awayScore !== ''
      )
      .map(({ matchId, account, homeScore, awayScore, firstGoal }) => {
        const matchDetails = matchInfo.find(
          (match) => parseInt(match.matchId) === parseInt(matchId)
        );

        const payload = {
          matchId,
          homeScore,
          awayScore,
          account,
          ip,
          fp,
          homeName: matchDetails ? matchDetails.homeName : 'Unknown',
          awayName: matchDetails ? matchDetails.awayName : 'Unknown',
          site: SITE
        };

        // Chỉ thêm firstGoal vào payload nếu có giá trị
        if (firstGoal !== undefined && firstGoal !== null) {
          payload.firstGoal = firstGoal;
        }

        return payload;
      });
  };

  const onFinishCountDown = () => {
    console.log('finished!');
  };

  const showListItemMatch = (matchList) => {
    // console.log('matchList:', matchList);
    if (!matchList || matchList.length <= 0) {
      return <p className="text-center">Hiện tại không có trận đấu nào để dự đoán</p>;
    } else {
      if (!matchList || matchList.length <= 0) {
        return (
          <div className="flex flex-col items-center justify-center py-8">
            <InboxOutlined className="text-6xl text-gray-300 mb-4" />
            <p className="text-center text-gray-500">Không có trận đấu nào để dự đoán</p>
          </div>
        );
      } else {
        const filteredMatchList = matchList;

        const itemMatch = filteredMatchList
          .filter((item) => timeServer <= item.countdown) // Filter out matches that have ended
          .map((item, index) => {
            const matchDateLabel = dayjs(item.startTime * 1000).format('HH:mm - DD/MM/YYYY');
            const endTimeLabel = dayjs(item.startTime * 1000).format('HH:mm:ss');
            return (
              <div
                className={twMerge(
                  'relative flex items-center w-full max-w-[650px] mx-auto px-0 md:px-3 text-sm font-medium flex-col pb-2 pt-2 mt-0 overflow-hidden ',
                  timeServer > item.countdown ? 'contrast-50' : ''
                )}
                key={index}
              >
                {/* League ribbon */}
                <div className="w-full flex justify-center">
                  <div
                    className="px-6 py-2 inline-flex items-center justify-center w-fit max-w-full"
                    style={{
                      backgroundImage: `url(/assets/images/match/bg_league_2.png)`,
                      backgroundSize: '100% 100%',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center'
                    }}
                  >
                    <p className="text-white mt-1 font-semibold uppercase text-center text-sm sm:text-base tracking-wide">
                      {item?.leagueName}
                    </p>
                  </div>
                </div>

                {/* Team bar */}
                <div className="relative w-full max-w-[980px] -mt-1 px-2">
                  <img
                    src="/assets/images/match/vs.png"
                    alt="VS"
                    className="absolute left-1/2 top-[-1.25px] transform -translate-x-1/2 w-auto h-[44px] z-0"
                  />
                  <div
                    className="relative w-full flex items-center justify-between px-4 py-2 h-[52px] z-10
                  "
                    style={{
                      backgroundImage: `url(/assets/images/match/bg_header.png)`,
                      backgroundSize: '100% 100%',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center'
                    }}
                  >
                    <div className="flex-1 text-[#2f2f2f] font-bold uppercase text-center text-base md:text-lg leading-tight">
                      {item.homeName}
                    </div>
                    <div className="shrink-0 w-[25%] h-full"></div>
                    <div className="flex-1 text-[#2f2f2f] font-bold uppercase text-center text-base md:text-lg leading-tight">
                      {item.awayName}
                    </div>
                  </div>
                </div>
                <div
                  className="flex flex-col -mt-2 items-center justify-center w-[90%] mx-auto"
                  style={{
                    backgroundImage: `url(/assets/images/match/bg_item.png)`,
                    backgroundSize: '100% 100%',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center'
                  }}
                >
                  {/* Date pill */}
                  <div
                    className="mt-2 px-5 py-1"
                    style={{
                      backgroundImage: `url(/assets/images/match/bg_time.png)`,
                      backgroundSize: '100% 100%',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center'
                    }}
                  >
                    <p className="text-white font-semibold text-sm sm:text-base text-center whitespace-nowrap">
                      {matchDateLabel}
                    </p>
                  </div>

                  {/* Time info pill */}
                  <div className="mt-2 bg-white w-full rounded-full max-w-[96%] md:max-w-[90%] px-2 shrink-0 md:px-4 py-2 flex flex-col sm:flex-row items-center justify-center gap-1 md:gap-2 sm:gap-4">
                    <div className="flex items-center gap-1 md:gap-2">
                      <span className="text-[#2c2c2c] text-[11px] md:text-[13px]">
                        Thời gian dự đoán còn:
                      </span>
                      <div className="bg-[color:var(--color-primary)] rounded-[6.862px] px-[4px] md:px-3 py-0.5">
                        <Countdown
                          value={item.countdown}
                          onFinish={onFinishCountDown}
                          format="HH:mm:ss"
                          style={{ color: '#fff', fontSize: '12px', fontWeight: 600 }}
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-1 md:gap-2">
                      <span className="text-[#2c2c2c] text-[11px] md:text-[13px]">
                        Thời gian kết thúc:
                      </span>
                      <div className="bg-[color:var(--color-primary)] rounded-[6.862px] px-[4px] md:px-3 py-0.5">
                        <span className="text-white text-[12px] font-semibold whitespace-nowrap">
                          {endTimeLabel}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Logos + score */}
                  <div className="mt-4 w-full max-w-[980px] flex items-center justify-between gap-0 md:gap-4 px-2">
                    <div className="bg-white rounded-full w-[48px] h-[48px] md:w-[64px] md:h-[64px] lg:w-[96px] lg:h-[96px] flex items-center justify-center md:ml-2 shadow-[0_2px_12px_rgba(0,0,0,0.08)]">
                      <img src={item.homeLogo} className="w-[70%] h-[70%] object-contain" alt="" />
                    </div>

                    <div className="flex flex-col items-center justify-center gap-3 flex-1">
                      <div className="flex items-center justify-center gap-3">
                        <Form.Item className="m-0 p-0" name={`homeScore_${item.matchId}`}>
                          <InputNumber
                            disabled={timeServer > item.countdown}
                            controls={false}
                            min={0}
                            max={99}
                            maxLength={2}
                            placeholder="-"
                            className="jun88-score-input !w-[54px] h-[34px]"
                            style={{ width: '54px' }}
                          />
                        </Form.Item>

                        <span className="text-[#2a2a2a] font-semibold text-base">Tỷ số</span>

                        <Form.Item className="m-0 p-0" name={`awayScore_${item.matchId}`}>
                          <InputNumber
                            disabled={timeServer > item.countdown}
                            controls={false}
                            min={0}
                            max={99}
                            maxLength={2}
                            placeholder="-"
                            className="jun88-score-input !w-[54px] h-[34px]"
                            style={{ width: '54px' }}
                          />
                        </Form.Item>
                      </div>

                      {/* First goal selector */}
                      <div className="!max-w-[320px] w-fit !border-solid !border-[0.6px] !border-[#D2D2D2] rounded-[6.273px] overflow-hidden flex items-stretch ">
                        <div className="flex-1 bg-[#E9F3FF] px-[4px] md:px-3 h-[30px] flex items-center justify-center">
                          <span className="text-[#767676] h-full pt-[6px] text-center text-[10px] md:text-[11px] font-medium whitespace-nowrap">
                            Đội có bàn thắng đầu tiên
                          </span>
                        </div>
                        <Form.Item className="m-0" name={`firstGoal_${item.matchId}`}>
                          <Select
                            defaultValue=""
                            className="!border-none !h-full !px-0 [&_.ant-select-selection-item]:!text-[10px] [&_.ant-select-selection-item]:md:!text-[11px] [&_.ant-select-selection-placeholder]:!text-[10px] [&_.ant-select-selection-placeholder]:md:!text-[11px]"
                            style={{ width: 100, height: 30 }}
                            options={[
                              { value: '', label: 'Lựa chọn' },
                              { value: 'home', label: item.homeName },
                              { value: 'away', label: item.awayName }
                            ]}
                          />
                        </Form.Item>
                      </div>
                    </div>

                    <div className="bg-white rounded-full w-[48px] h-[48px] md:w-[64px] md:h-[64px] lg:w-[96px] lg:h-[96px] flex items-center justify-center md:mr-2 shadow-[0_2px_12px_rgba(0,0,0,0.08)]">
                      <img src={item.awayLogo} className="w-[70%] h-[70%] object-contain" alt="" />
                    </div>
                  </div>

                  <div className="mt-3 mb-5 flex justify-center">
                    <Button
                      type="primary"
                      htmlType="submit"
                      className="jun88-prediction-submit p-0 border-0 bg-transparent h-auto overflow-hidden"
                    >
                      <img
                        src="/assets/images/match/btnDuDoan.png"
                        alt="GỬI DỰ ĐOÁN"
                        className="w-auto h-[41px]"
                      />
                    </Button>
                  </div>
                </div>
              </div>
            );
          });
        if (!itemMatch || itemMatch.length <= 0) {
          return (
            <div className="flex flex-col items-center justify-center py-8">
              <InboxOutlined className="text-6xl text-gray-300 mb-4" />
              <p className="text-center text-gray-500">Không có trận đấu nào để dự đoán</p>
            </div>
          );
        } else {
          return (
            <div className="w-full h-full grid grid-cols-1 lg:grid-cols-2 gap-6 mt-2 md:px-3 items-center justify-center">
              {itemMatch}
            </div>
          );
        }
      }
    }
  };

  return (
    <div className={twMerge(SITE, 'w-full h-full')}>
      <img
        src="bg.png"
        alt="Background"
        className="w-full h-full fixed right-0 bottom-0 object-cover object-center"
      />

      <RightGif />

      {isPredictConfirmOpen ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-3">
          <div
            className="absolute inset-0 bg-black/20"
            aria-hidden="true"
            onClick={isLoadingSubmit ? undefined : onCancelMatchConfirm}
          />

          <div
            role="dialog"
            aria-modal="true"
            aria-label="Xác nhận dự đoán trận đấu"
            className="relative w-full max-w-[520px] bg-[#d4edf7] border-[2.289px] border-solid border-white rounded-[10px] p-[18px] md:p-[30px] backdrop-blur-[24.266px]"
          >
            <div className="flex justify-center mb-3">
              <div className="w-[68px] h-[68px] md:w-[80px] md:h-[80px] bg-[#4cd964] rounded-full flex items-center justify-center shadow-lg">
                <svg
                  className="w-[40px] h-[40px] md:w-[50px] md:h-[50px] text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>

            <div className="text-center font-extrabold text-[#26a9e0] text-[20px] md:text-[24px] leading-[32px] md:leading-[40px]">
              XÁC NHẬN DỰ ĐOÁN
            </div>

            <div className="mt-2 text-center text-[#2a2a2a] font-medium text-[14px] md:text-[16px] leading-[22px] md:leading-[26px]">
              <div className="mb-2">Bạn đã chọn dự đoán các trận đấu sau:</div>

              <div className="max-h-[300px] overflow-y-auto space-y-2 px-2">
                {pendingMatchPredictions.map((match, index) => (
                  <div key={match.matchId || index} className="bg-white/60 rounded-lg p-2 md:p-3">
                    <div className="font-bold text-[#333]">
                      {match.homeName} - {match.awayName}
                    </div>
                    <div className="text-[#26a8df] font-bold text-[16px] md:text-[18px]">
                      Tỷ số: {match.homeName}{' '}
                      <span className="text-[#e74c3c]">{match.homeScore}</span> -{' '}
                      <span className="text-[#e74c3c]">{match.awayScore}</span> {match.awayName}
                    </div>
                    {match.firstGoal && (
                      <div className="text-[#666] text-[12px] md:text-[14px]">
                        Đội ghi bàn đầu tiên:{' '}
                        <span className="font-bold text-[#26a8df]">
                          {match.firstGoal === 'home' ? match.homeName : match.awayName}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-3 italic text-[13px] md:text-[14px]">
                Hãy cùng chờ đợi kết quả nhé.
              </div>
            </div>

            <div className="mt-4 flex items-center justify-center gap-[18px]">
              <button
                type="button"
                disabled={isLoadingSubmit}
                onClick={onConfirmMatchPrediction}
                className={twMerge(
                  'w-[143.5px] h-[38px] md:h-auto border-none bg-[#26a8df] text-white rounded-[9.965px] px-[9.965px] py-[9.965px] font-semibold text-[13.951px]',
                  isLoadingSubmit ? 'opacity-60 cursor-not-allowed' : ''
                )}
              >
                {isLoadingSubmit ? 'ĐANG GỬI...' : 'XÁC NHẬN'}
              </button>

              <button
                type="button"
                disabled={isLoadingSubmit}
                onClick={onCancelMatchConfirm}
                className={twMerge(
                  'w-[143.5px] h-[38px] md:h-auto border-none bg-[#a2b1b8] text-white rounded-[9.965px] px-[9.965px] py-[9.965px] font-semibold text-[13.951px]',
                  isLoadingSubmit ? 'opacity-60 cursor-not-allowed' : ''
                )}
              >
                HỦY BỎ
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="pb-[110px] md:pb-3 md:px-3 relative z-10 w-full max-w-[1325px] mx-auto">
        <Header />

        <div className="mx-auto">
          <div className="w-full h-full">
            <Form
              name="formMatch"
              onFinish={onFinishForm}
              onFinishFailed={onFinishFailed}
              autoComplete="off"
              form={form}
              initialValues={{ account: '' }}
            >
              <div className="w-full h-full">
                <div className="flex flex-col bg-transparent px-2 pb-2 w-full md:px-6 h-full items-center">
                  <div className="flex flex-col w-full h-full items-center px-3 justify-center gap-2 md:mx-3">
                    <div className="w-full max-w-[520px] flex flex-row gap-2 md:gap-3 md:py-2 items-center justify-center">
                      <Form.Item name="account" className="mb-0">
                        <Input
                          placeholder="Tên đăng nhập ..."
                          maxLength={20}
                          prefix={<UserOutlined />}
                          className="jun88-auth-input w-full !max-w-[240px]"
                          rules={[
                            {
                              required: true,
                              message: 'Vui lòng điền tên đăng nhập!'
                            }
                          ]}
                          onBlur={() => {
                            const v = form.getFieldValue('account');
                            const trimmed = typeof v === 'string' ? v.trim() : '';
                            if (trimmed) {
                              localStorage.setItem('prediction_account', trimmed);
                            } else {
                              localStorage.removeItem('prediction_account');
                            }
                          }}
                        />
                      </Form.Item>
                      <Form.Item className="mb-0 shrink-0">
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={isLoadingSubmit}
                          className="jun88-auth-btn text-white font-semibold border-0 w-full sm:w-auto"
                        >
                          Xác Nhận
                        </Button>
                      </Form.Item>
                    </div>

                    <div className="w-full flex justify-center mt-1 md:mt-2">
                      <div
                        role="tablist"
                        className="w-full max-w-[500px] !border-2 !border-white rounded-[30px] overflow-hidden flex items-stretch"
                      >
                        <button
                          role="tab"
                          aria-selected={predictionMode === 'match'}
                          type="button"
                          onClick={() => {
                            setPredictionMode('match');
                            fetchDataScheduByLS();
                          }}
                          className={twMerge(
                            'w-1/2 p-[10px] flex items-center justify-center !border-2 !border-white rounded-tl-[30px] rounded-bl-[30px] focus:outline-none',
                            predictionMode === 'match'
                              ? 'bg-[color:var(--color-segmented-tab-active)]'
                              : 'bg-[color:var(--color-segmented-tab-inactive)]'
                          )}
                        >
                          <span className="font-bold leading-normal text-[13px] md:text-[16px] text-white whitespace-nowrap">
                            Dự đoán theo trận
                          </span>
                        </button>

                        <button
                          role="tab"
                          aria-selected={predictionMode === 'worldcup'}
                          type="button"
                          onClick={() => setPredictionMode('worldcup')}
                          className={twMerge(
                            'w-1/2 p-[10px] flex items-center justify-center !border-2 !border-white rounded-tr-[30px] rounded-br-[30px] focus:outline-none',
                            predictionMode === 'worldcup'
                              ? 'bg-[color:var(--color-segmented-tab-active)]'
                              : 'bg-[color:var(--color-segmented-tab-inactive)]'
                          )}
                        >
                          <span className="font-bold leading-normal text-[13px] md:text-[16px] text-white whitespace-nowrap">
                            Dự đoán World Cup
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mx-auto lg:p-2 w-full md:pb-8 md:pt-2">
                  <div className="w-full h-full">
                    {isLoading ? (
                      <div className="w-full h-full text-center py-8">
                        <Spin />
                      </div>
                    ) : (
                      <div className="w-full h-full flex flex-col gap-0 lg:gap-2 mt-1">
                        {predictionMode === 'match' ? (
                          showListItemMatch(getFilteredMatches())
                        ) : (
                          <WorldCupPredictionBlock
                            ref={worldCupRef}
                            form={form}
                            disabled={isLoadingSubmit}
                            IP={IP}
                            FP={FP}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Form>
          </div>
        </div>
      </div>
      <NavBar />
    </div>
  );
};

export default PredictPage;
